"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const CONTACT_INFO = {
  phone: "+221 78 166 66 38",
  email: "contact@kbccoffeeservice.com",
  address: "Basé à Thiès",
  address1: "Livraison gratuite à Thiès à partir de 25 000 FCFA",
  address2: "Dakar & Petite Côte : frais selon commande",
  hours: "Lun - Ven: 08h30 - 18h00 | Sam: 09h00 - 13h00",
  whatsapp: "221781666638"
};
const PROJECT_TYPES = ["Achat capsules", "Achat machine", "Offre entreprise", "Demande de devis personnalisé"];
const whatsappMessage = `Bonjour KBC Coffee Service 

Je souhaite des informations ou passer commande.

Type de demande :
- Capsules / Machine / Offre entreprise

Localisation :
- Thiès / Dakar / Petite Côte

Pouvez-vous me proposer une offre adaptée ?

Merci.`;
const whatsappUrl = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(whatsappMessage)}`;
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="4854" className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="h-16 w-16 bg-[hsl(35,40%,92%)] rounded-full flex items-center justify-center">
          <CircleCheck className="h-8 w-8 text-[hsl(43,70%,50%)]" />
        </div>
        <h3 className="text-xl font-serif font-semibold text-foreground">
          Message envoyé !
        </h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-[hsl(25,20%,40%)] text-[hsl(25,20%,40%)] hover:bg-[hsl(35,40%,92%)]">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Le nom complet est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(25,30%,12%)]">Nom complet</FormLabel>
              <FormControl>
                <input className="flex h-10 w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors" placeholder="Ex: Moussa Diop" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Le numéro de téléphone est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(25,30%,12%)]">
                Numéro de téléphone
              </FormLabel>
              <FormControl>
                <input type="tel" className="flex h-10 w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors" placeholder="+221 77 123 45 67" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "L'adresse e-mail est requise",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Adresse e-mail invalide"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(25,30%,12%)]">Adresse e-mail</FormLabel>
            <FormControl>
              <input type="email" className="flex h-10 w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors" placeholder="moussa@exemple.sn" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="project_type" rules={{
        required: "Veuillez sélectionner un type de demande"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(25,30%,12%)]">
                Objet de votre demande
              </FormLabel>
              <FormControl>
                <select className="flex h-10 w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors" {...field}>
                  <option value="" disabled>
                    Sélectionnez une option
                  </option>
                  {PROJECT_TYPES.map((type, i) => <option key={i} value={type} data-index={i}>
                      {type}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="budget" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-[hsl(25,30%,12%)]">
                Budget estimé (FCFA)
              </FormLabel>
              <FormControl>
                <input type="number" className="flex h-10 w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 transition-colors" placeholder="Ex. 50000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="description" rules={{
      required: "La description est requise"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[hsl(25,30%,12%)]">
              Détails de votre demande
            </FormLabel>
            <FormControl>
              <textarea className="flex min-h-[120px] w-full rounded-xl border-b border-t-0 border-x-0 border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:border-[hsl(43,70%,50%)] disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-colors" placeholder="Ex : nombre de collaborateurs, consommation mensuelle, type de capsules souhaitées..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2 text-red-800 text-sm">
          <CircleAlert className="h-5 w-5 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" className="w-full bg-primary hover:bg-[hsl(43,70%,50%)] text-primary-foreground rounded-xl transition-all shadow-md" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Envoi en cours...
          </> : "Envoyer ma demande"}
      </Button>
    </Form>;
}
export default function ParlonsDeVotreProjet() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="nous-contacter" className="py-16 md:py-24 bg-[hsl(35,40%,98%)] text-foreground relative overflow-hidden" ref={ref}>
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-[hsl(43,70%,50%)] blur-[120px] opacity-10" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-[hsl(25,30%,12%)] blur-[100px] opacity-5" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`grid lg:grid-cols-2 gap-12 items-start transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-[hsl(25,30%,12%)]">
                Une question ou une commande ?
              </h2>
              <p className="text-lg text-[hsl(25,20%,40%)] leading-relaxed">
                Basés à Thiès, nous accompagnons particuliers et entreprises dans
                le choix de leurs capsules et machines à café.
              </p>
              <p className="text-lg text-[hsl(25,20%,40%)] leading-relaxed">
                Contactez-nous directement sur WhatsApp pour une réponse rapide.
              </p>
            </div>

            {/* WhatsApp prioritaire */}
            <Card className="border-[hsl(43,70%,50%)]/30 shadow-md bg-card/80 backdrop-blur-sm rounded-xl overflow-hidden">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-full bg-green-500/10 text-green-600 shrink-0">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-[hsl(25,30%,12%)] text-2xl font-serif">
                      WhatsApp Direct
                    </CardTitle>
                    <CardDescription className="text-[hsl(25,20%,40%)]">
                      Réponse rapide pour commandes, devis et demandes urgentes
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm text-[hsl(25,20%,40%)]">
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Commande rapide</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Conseils personnalisés</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Réponse en quelques minutes</span>
                  </div>
                </div>

                <Button asChild size="lg" className="w-full bg-green-600 hover:bg-green-700 text-white shadow-lg border-0 rounded-xl transition-all">
                  <Link to={whatsappUrl} newTab>
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Écrire sur WhatsApp
                  </Link>
                </Button>

                <p className="text-xs text-[hsl(25,20%,40%)] italic">
                  Le moyen le plus rapide pour commander ou demander un devis.
                </p>
              </CardContent>
            </Card>

            {/* Email secondaire */}
            <div className="flex items-start gap-4 p-5 rounded-xl border border-border bg-card/40 backdrop-blur-sm hover:bg-card transition-all shadow-sm">
              <div className="p-3 rounded-full bg-[hsl(43,70%,50%)]/10 text-[hsl(43,70%,50%)] shrink-0">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-serif font-semibold text-[hsl(25,30%,12%)] mb-1">
                  Email
                </h3>
                <p className="text-muted-foreground text-sm mb-2">
                  Réponse sous 24h ouvrées
                </p>
                <Link to={`mailto:${CONTACT_INFO.email}`} className="text-lg font-medium text-[hsl(25,30%,12%)] hover:text-[hsl(43,70%,50%)] transition-colors">
                  {CONTACT_INFO.email}
                </Link>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 p-5 rounded-xl border border-border bg-card/40 backdrop-blur-sm">
                <MapPin className="h-5 w-5 text-[hsl(43,70%,50%)] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-serif font-medium text-[hsl(25,30%,12%)]">
                    Zone de service
                  </h3>
                  <p className="text-sm text-muted-foreground">{CONTACT_INFO.address}</p>
                  <p className="text-sm text-muted-foreground">{CONTACT_INFO.address1}</p>
                  <p className="text-sm text-muted-foreground">{CONTACT_INFO.address2}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-5 rounded-xl border border-border bg-card/40 backdrop-blur-sm">
                <Clock className="h-5 w-5 text-[hsl(43,70%,50%)] shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-serif font-medium text-[hsl(25,30%,12%)]">
                    Horaires
                  </h3>
                  <p className="text-sm text-muted-foreground">{CONTACT_INFO.hours}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(43,70%,50%)]/10 to-[hsl(25,30%,12%)]/5 rounded-2xl transform rotate-1 scale-[1.02] -z-10" />
            <Card className="border-border/60 shadow-xl bg-card/95 backdrop-blur-sm rounded-xl overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-serif text-[hsl(25,30%,12%)]">
                  Formulaire de Contact
                </CardTitle>
                <CardDescription className="text-[hsl(25,20%,40%)]">
                  Sélectionnez vos besoins et nous reviendrons vers vous avec une
                  offre personnalisée.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="contact-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_type: "",
                description: "",
                budget: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true
                },
                project_type: {
                  required: true
                },
                description: {
                  required: true
                }
              }} submitText="Envoyer ma demande" successMessage="Merci ! Votre demande a bien été reçue. KBC Coffee Service vous contactera très prochainement." errorMessage="Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous contacter via WhatsApp.">
                  <ProjectForm />
                </FormProvider>

                <div className="mt-6 pt-4 border-t border-border/40 flex flex-col gap-2 text-xs text-[hsl(25,20%,40%)]">
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Réponse rapide sous 24h</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Livraison fiable depuis Thiès</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[hsl(43,70%,50%)] shrink-0" />
                    <span>Solutions adaptées aux entreprises</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}